/* Map Widget Module */

import cloneDeep from 'lodash/cloneDeep';
import d3Utils from '../../../../utils/d3Utils';
import chartUtils from '../../../../utils/chart-utils';
import topojson from '../../../../utils/topojson';
import worldMapConfigBuilder from './map-config-builder';
import { worldMapTooltip, usMapTooltip } from '../../widget-utilities/widget-tooltip';

var mapLeaflet = function mapLeaflet() {
    // Render Map widget
    var renderWidget = function renderWidget(widgetConfig) {
        var ContainerId = widgetConfig.ContainerId,
            Model = widgetConfig.Model,
            Width = widgetConfig.Width,
            Height = widgetConfig.Height,
            Cloud = widgetConfig.Cloud,
            StampText = widgetConfig.StampText,
            ColorScheme = widgetConfig.ColorScheme,
            MapConfig = widgetConfig.MapConfig,
            Data = widgetConfig.Data;


        var el = window.document.querySelector('#' + ContainerId);
        d3.select(el).select('svg').remove();

        var parentContainerId = ContainerId.includes('flip-') ? ContainerId.split('flip-')[1] : ContainerId;
        // track last translation and scale event we processed
        var tlast = [0, 0],
            slast = null;

        var data = null;
        var mapData = JSON.parse(chartUtils.replaceAll(JSON.stringify(MapConfig.MapData), '\x01', ''));
        var markerLatitudeFieldName = MapConfig.LatitudeField;
        var markerLongitudeFieldName = MapConfig.LongitudeField;
        var markerIdFieldName = MapConfig.MarkerField;
        var markerNameFieldName = MapConfig.MarkerLabelField;
        var modelName = Model;
        var cloudName = Cloud;
        var crumbMetaName = MapConfig.MarkerCrumbMetaName;
        var crumbFieldName = MapConfig.MarkerCrumbFieldName;
        var crumbDisplayFieldName = MapConfig.CrumbDisplayFieldName;
        var crumbFormat = {
            CloudName: cloudName,
            ModelName: modelName,
            Type: 'Node',
            Name: crumbMetaName,
            FieldName: crumbFieldName,
            Value: '',
            DisplayFieldName: crumbDisplayFieldName,
            DisplayValue: null
        };
        var regionCrumbMetaName = MapConfig.RegionCrumbMetaName;
        var regionCrumbFieldName = MapConfig.RegionCrumbFieldName;
        var regionCrumbDisplayFieldName = MapConfig.RegionCrumbDisplayFieldName;
        var regionCrumbFormat = {
            CloudName: cloudName,
            ModelName: modelName,
            Type: 'Node',
            Name: regionCrumbMetaName,
            FieldName: regionCrumbFieldName,
            Value: '',
            DisplayFieldName: regionCrumbDisplayFieldName,
            DisplayValue: null
        };
        var markerType = MapConfig.MarkerType;
        var markerColor = MapConfig.MarkerColor;
        var markerClassifierField = MapConfig.MarkerClassifierFieldName;
        var markerScaleField = MapConfig.MarkerScaleFieldName;
        var markerPieValueField = MapConfig.MarkerValueFieldName;
        var tipField = MapConfig.TipField;
        var stampText = StampText;
        var pieFieldNames = MapConfig.PieFieldNames;
        var mapClassifierName = MapConfig.MapClassifierName;
        var tooltip = "tooltip";
        var colorScheme = JSON.parse(chartUtils.replaceAll(JSON.stringify(ColorScheme), '\x01', ""));

        var enhancedMapUSCenter = null;
        var enhanedMapWorldCenter = null;

        //Sice Alaska has max longitude more than 180, it is
        //trying to draw map with less scale and
        //rotating 90 degrees.
        var maxUSLongitude = -65.221110;

        var connectionsData = MapConfig.ConnectionSeriesData;
        var scaleFactor = Number(MapConfig.ScaleCoefficient);

        var markerSeriesConfig = MapConfig.MarkerSeries;
        if (markerSeriesConfig != null && markerSeriesConfig.toString().trim().length > 0) markerSeriesConfig = JSON.parse(chartUtils.replaceAll(JSON.stringify(markerSeriesConfig), '\x01', ''));

        var markerSeriesData = MapConfig.MarkerSeriesData;

        var heatMapSeriesConfig = MapConfig.HeatMapSeries;
        if (heatMapSeriesConfig != null && heatMapSeriesConfig.toString().trim().length > 0) heatMapSeriesConfig = JSON.parse(chartUtils.replaceAll(JSON.stringify(heatMapSeriesConfig), '\x01', ''));

        var heatMapSeriesData = MapConfig.HeatMapSeriesData;

        var fromMarkerField = MapConfig.FromMarkerField;
        var toMarkerField = MapConfig.ToMarkerField;
        var connectionClassifierField = MapConfig.ConnectionClassifierField;

        var connectionSeriesConfig = MapConfig.ConnectionSeries;
        if (connectionSeriesConfig != null && connectionSeriesConfig.toString().trim().length > 0) connectionSeriesConfig = JSON.parse(chartUtils.replaceAll(JSON.stringify(connectionSeriesConfig), '\x01', ''));

        var connectionSeriesData = MapConfig.ConnectionSeriesData;

        var pieRadius = 0.04;
        var margin = {
            top: 0.02,
            bottom: 0.01,
            right: 0.02,
            left: 0.02
        };

        var colorScheme = JSON.parse(chartUtils.replaceAll(JSON.stringify(ColorScheme), '\x01', ""));

        if (markerPieValueField.trim().length == 0) markerPieValueField = null;

        if (tipField.trim().length == 0) tipField = null;

        var width = Width;
        var height = 1 - margin.bottom;

        height = chartUtils.makeRelative(height, Height);
        var centerCoordinates = null;
        var centerCoordinatesUi = JSON.parse(MapConfig.CenterCoordinates);
        var calcCenterCoords = true;
        var mapType = MapConfig.MapType.toString();
        var mapViewType = MapConfig.MapViewType.toString();
        var mergeData = JSON.parse(chartUtils.replaceAll(MapConfig.MergeData, '\x01', ''));
        var mergeKeyName = MapConfig.MergeMatchPropertyName;

        //d3 supports max zoom scale value of 45600.
        var maximumZoomScale = 45600;
        var zoomMargin = 0.9;
        var zoom = MapConfig.ZoomFactor.toString();
        var setAutoZoom = true;
        var zoomFactor = null;
        var showLegend = MapConfig.ShowLegend;
        var legendScale = Math.min(width, height);
        var legendRectSize = 0.03 * legendScale;

        if (isNaN(scaleFactor) || scaleFactor <= 0) scaleFactor = 1;

        pieRadius = chartUtils.makeRelative(pieRadius, Math.min(width, height));
        var svg = null;
        var map = null;
        var leafletMap = null;

        var scale = Math.max(width, height);

        if (centerCoordinatesUi != null) {
            calcCenterCoords = false;
            enhancedMapUSCenter = centerCoordinatesUi;
            enhanedMapWorldCenter = centerCoordinatesUi;
            centerCoordinates = centerCoordinatesUi;
        } else {
            enhancedMapUSCenter = [39.8283, -98.5795];
            enhanedMapWorldCenter = [51.4826, 0];
        }

        if (zoom != null && zoom != 0) {
            setAutoZoom = false;
            zoomFactor = zoom;
        }

        var projection = d3.geo.albers().scale(scale).translate([width / 2, height / 2]);

        if (mapType.toLowerCase() == "world") {
            zoomMargin = 0.1;
            scale = Math.max(width, height) / 8;
            projection = d3.geo.mercator().scale(scale).translate([width / 2, height / 2]);
        }

        var minLatitude = 90;
        var maxLatitude = -90;
        var minLongitude = 180;
        var maxLongitude = -180;
        var markers = [];
        var minScale = 0;
        var maxScale = 0;
        var markerClassifiers = [];

        var path = d3.geo.path().projection(projection);

        if (data != null) {
            for (var i = 0; i < JSON.parse(data.Stores).length; i++) {
                var item = JSON.parse(data.Stores)[i];

                if (item[markerLatitudeFieldName] == null || item[markerLongitudeFieldName] == null || item[markerNameFieldName] == null || item[markerIdFieldName] == null || Number(item[markerLatitudeFieldName]) == 0 || Number(item[markerLongitudeFieldName]) == 0 || isNaN(Number(item[markerLatitudeFieldName])) || isNaN(Number(item[markerLongitudeFieldName]))) continue;

                if (chartUtils.findValue(markers, "id", item[markerIdFieldName]) != null) continue;

                var value = {
                    lat: Number(item[markerLatitudeFieldName]),
                    long: Number(item[markerLongitudeFieldName]),
                    id: item[markerIdFieldName],
                    name: item[markerNameFieldName]
                };

                value["class"] = chartUtils.makeStyleName(markerClassifierField);

                if (markerType != "pie") {
                    if (markerScaleField) {
                        value["markerScaleField"] = markerScaleField;
                        value["actualScale"] = item[markerScaleField];
                        minScale = Math.min(minScale, Number(item[markerScaleField]));
                        maxScale = Math.max(maxScale, Number(item[markerScaleField]));
                    }

                    if (markerClassifierField.trim().length > 0 && item[markerClassifierField] != null) {
                        value["class"] = chartUtils.makeStyleName(item[markerClassifierField]);

                        if (markerClassifiers.indexOf(item[markerClassifierField]) < 0) markerClassifiers.push(item[markerClassifierField]);
                    }
                } else {
                    value.pieData = buildPieData(value.id, value.name);
                }

                value["crumbMetaName"] = crumbMetaName;
                value["crumbFieldName"] = crumbFieldName;
                value[crumbFieldName] = item[crumbFieldName];
                value["crumbDisplayFieldName"] = crumbDisplayFieldName;
                value[crumbDisplayFieldName] = item[crumbDisplayFieldName];
                value["color"] = markerColor;

                value["scaleFactor"] = scaleFactor;

                if (chartUtils.findValue(markers, 'id', value.id) != null) continue;

                markers.push(value);
            }
        }

        //Include other markers
        if (markerSeriesConfig != null && markerSeriesConfig.length > 0 && markerSeriesData != null) {

            for (var i = 0; i < markerSeriesConfig.length; i++) {
                var currentSeries = markerSeriesConfig[i];

                if (markerSeriesData[currentSeries.SeriesName] == null) continue;

                var seriesData = JSON.parse(markerSeriesData[currentSeries.SeriesName]);

                if (seriesData == null || seriesData.length == 0) continue;

                var currentMarkerType = "circle";

                switch (currentSeries.MarkerType.toLowerCase()) {
                    case "c":
                    default:
                        currentMarkerType = "circle";
                        break;
                    case "d":
                        currentMarkerType = "diamond";
                        break;
                    case "tu":
                        currentMarkerType = "triangle-up";
                        break;
                    case "td":
                        currentMarkerType = "triangle-down";
                        break;
                    case "s":
                        currentMarkerType = "square";
                        break;
                }

                var tooltipConfigs = null;

                if (currentSeries.CompoundTipField != null) {
                    tooltipConfigs = currentSeries.CompoundTipField.TooltipConfigs;
                }

                var colorPaletteIndex = 0;
                var itemToColorMapping = {};

                for (var j = 0; j < seriesData.length; j++) {
                    var item = seriesData[j];

                    if (item == null) continue;

                    if (item[currentSeries.LatitudeField] == null || item[currentSeries.LongitudeField] == null || item[currentSeries.MarkerField] == null || Number(item[currentSeries.LatitudeField]) == 0 || Number(item[currentSeries.LongitudeField]) == 0 || isNaN(Number(item[currentSeries.LatitudeField])) || isNaN(Number(item[currentSeries.LongitudeField]))) continue;

                    if (calcCenterCoords || setAutoZoom) {
                        if (item[currentSeries.LatitudeField] >= Number(maxLatitude)) maxLatitude = item[currentSeries.LatitudeField];
                        if (item[currentSeries.LatitudeField] <= Number(minLatitude)) minLatitude = item[currentSeries.LatitudeField];

                        if (item[currentSeries.LongitudeField] >= Number(maxLongitude)) {
                            if (mapType.toLowerCase() != "world") {
                                //Max longitude should always less than maxUSLongitude for US map.
                                if (Number(item[currentSeries.LongitudeField]) < maxUSLongitude) maxLongitude = item[currentSeries.LongitudeField];
                            } else maxLongitude = item[currentSeries.LongitudeField];
                        }

                        if (item[currentSeries.LongitudeField] <= Number(minLongitude)) minLongitude = item[currentSeries.LongitudeField];
                    }

                    var value = {
                        lat: Number(item[currentSeries.LatitudeField]),
                        long: Number(item[currentSeries.LongitudeField]),
                        id: item[currentSeries.MarkerField],
                        name: item[currentSeries.MarkerLabelField],
                        markerType: currentMarkerType
                    };

                    if (currentSeries.MarkerClassifierFieldName) value["class"] = item[currentSeries.MarkerClassifierFieldName];else value["class"] = chartUtils.makeStyleName(chartUtils.replaceAll(currentSeries.SeriesName, " ", ""));

                    value["crumbMetaName"] = currentSeries.CrumbMetaName;
                    value["crumbFieldName"] = currentSeries.CrumbFieldName;
                    value["MarkerLabelFieldName"] = currentSeries.MarkerLabelField;
                    value["MarkerField"] = currentSeries.MarkerField;
                    value["SeriesName"] = currentSeries.SeriesName;
                    value[currentSeries.CrumbFieldName] = item[currentSeries.CrumbFieldName];
                    value["crumbDisplayFieldName"] = currentSeries.CrumbDisplayFieldName;
                    value[currentSeries.CrumbDisplayFieldName] = item[currentSeries.CrumbDisplayFieldName];
                    value["scaleFactor"] = currentSeries.ScaleCoefficient;
                    value["counter"] = item["RecordCount"];
                    value["UseColor"] = currentSeries.UseColor;
                    value["ColorPalette"] = currentSeries.ColorPalette;
                    value["ColorScheme"] = colorScheme;
                    value["SeriesLengthCheck"] = markerSeriesConfig.length;

                    if (markerSeriesConfig.length < 2) {
                        if (currentSeries.ColorPalette && currentSeries.ColorPalette.length > 0 && !currentSeries.UseColor) {
                            if (!(item[currentSeries.MarkerField] in itemToColorMapping)) {
                                itemToColorMapping[item[currentSeries.MarkerField]] = currentSeries.ColorPalette[colorPaletteIndex];
                                colorPaletteIndex += 1;
                                if (colorPaletteIndex > currentSeries.ColorPalette.length - 1) colorPaletteIndex = 0;
                                value["color"] = itemToColorMapping[item[currentSeries.MarkerField]];
                            } else {
                                value["color"] = itemToColorMapping[item[currentSeries.MarkerField]];
                            }
                        } else if (colorScheme && colorScheme.length > 0 && !currentSeries.UseColor) {
                            if (!(item[currentSeries.MarkerField] in itemToColorMapping)) {
                                itemToColorMapping[item[currentSeries.MarkerField]] = colorScheme[colorPaletteIndex];
                                colorPaletteIndex += 1;
                                if (colorPaletteIndex > colorScheme - 1) colorPaletteIndex = 0;
                                value["color"] = itemToColorMapping[item[currentSeries.MarkerField]];
                            } else {
                                value["color"] = itemToColorMapping[item[currentSeries.MarkerField]];
                            }
                        } else {
                            value["color"] = currentSeries.Color;
                        }
                    } else {
                        value["color"] = currentSeries.Color;
                    }

                    if (currentSeries.MarkerScaleFieldName) {
                        value["markerScaleField"] = currentSeries.MarkerScaleFieldName;
                        value["actualScale"] = item[currentSeries.MarkerScaleFieldName];
                        minScale = Math.min(minScale, Number(item[currentSeries.MarkerScaleFieldName]));
                        maxScale = Math.max(maxScale, Number(item[currentSeries.MarkerScaleFieldName]));
                    }

                    if (tooltipConfigs != null) {
                        for (var tipIndex = 0; tipIndex < tooltipConfigs.length; tipIndex++) {
                            var tipItem = tooltipConfigs[tipIndex];

                            value[tipItem.Id] = chartUtils.getTooltipValue(tipItem, item);
                        }
                    }
                    var tipHeader = currentSeries.MarkerLabelField == null ? currentSeries.SeriesName : currentSeries.MarkerLabelField;

                    if (item[tipField] != null) tipHeader = item[tipField] + "<br /> " + tipHeader;

                    var displayName = item[currentSeries.MarkerLabelField] == null ? item[currentSeries.MarkerField] : item[currentSeries.MarkerLabelField];

                    var defaultTipText = "<span class='tip-header'>" + tipHeader + ": </span><span class='tip-value'> " + displayName + "</span>";
                    var tooltip = currentSeries.CompoundTipField;
                    var tipText;
                    if (currentSeries.MarkerLabelField != null) tipText = chartUtils.buildTooltipText(tooltip, value, defaultTipText, currentSeries.MarkerLabelField, currentSeries.AggregationType, currentSeries.TotalRecords);else tipText = chartUtils.buildTooltipText(tooltip, value, defaultTipText, currentSeries.MarkerField, currentSeries.AggregationType, currentSeries.TotalRecords);
                    if (!tipText.indexOf(undefined) > -1) value["tooltipText"] = tipText;

                    markers.push(value);
                }

                currentSeries["itemToColorMapping"] = itemToColorMapping;
            }
        }

        if (mapViewType.toLowerCase() !== "standard") {
            var accessToken = Data.MapAccessToken;
            var enhancedMapUrl = '{EnhancedMapUrl}';

            var tileUrl = enhancedMapUrl + '?access_token=' + accessToken;
            L.Map.prototype.setSize = function (width, height) {
                this._size = new L.Point(width, height);
                // reset pixelOrigin
                this._resetView(this.getCenter(), this.getZoom());
                return this;
            };

            var center = null;

            switch (mapType.toLowerCase()) {
                case "world":
                    center = enhanedMapWorldCenter;
                    break;
                default:
                    center = enhancedMapUSCenter;
            }

            if (el._leaflet_id) {
                el._leaflet_id = null;
            }

            leafletMap = L.map(el).setView(center, 1);
            leafletMap.setSize(width, height);

            svg = d3.select(leafletMap.getPanes().overlayPane).append("svg").attr('width', width).attr('height', height).attr('viewBox', '0 0 ' + width + ' ' + height).attr('preserveAspectRatio', 'xMidYMid').attr('marktype', markerType).attr('class', function () {
                return mapClassifierName == "" ? 'map-ocean' : 'map-ocean ' + mapClassifierName;
            }).attr('radius', pieRadius).attr('innerradius', 0).attr('mapviewtype', mapViewType);

            map = svg.select('g').attr('class', '    ');

            svg.attr("enhancedMapUrl", tileUrl);
            svg.attr("mapAccessToken", accessToken);
            svg.attr('centerLocation', JSON.stringify(enhanedMapWorldCenter));
        }

        svg.attr('scale', scale);

        if (mapType.toLowerCase() == "world") {
            svg.attr('zoomFactor', zoom);

            map.selectAll('.subunit').data(topojson.feature(mapData, mapData.objects.countries).features).enter().append('path').attr('d', path).attr('values', function (d) {
                return JSON.stringify(d);
            }).attr('class', 'geo-region world dataviz').on("touchstart", function () {
                usMapTooltip.onMouseOver.call(this, parentContainerId);
            }).on("mouseover", function () {
                usMapTooltip.onMouseOver.call(this, parentContainerId);
            }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                usMapTooltip.onMouseMove.call(this, parentContainerId);
            });

            map.append('path').datum(topojson.mesh(mapData, mapData.objects.countries, function (a, b) {
                return a !== b;
            })).attr('d', path).attr('values', function (d) {
                return JSON.stringify(d);
            }).attr('class', 'geo-borders');
        }
        if (mapType.toLowerCase() == "usstates") {
            svg.attr('zoomFactor', zoom);
            if (mergeData == null) {
                map.append("g").selectAll("path").data(topojson.feature(mapData, mapData.objects.states).features).enter().append("path").attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-region state dataviz").on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });

                map.append("path").datum(topojson.mesh(mapData, mapData.objects.states, function (a, b) {
                    return a !== b;
                })).attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-borders state-border");
            } else {
                map.append("g").selectAll("path").data(topojson.feature(mapData, mapData.objects.states).features.filter(function (d) {
                    var id = chartUtils.findValue(mergeData, mergeKeyName, d.id);
                    if (id == null) return false;
                    return true;
                })).enter().append("path").attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-region state dataviz").on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });

                map.append("path").datum(topojson.mesh(mapData, mapData.objects.states, function (d) {
                    var id = chartUtils.findValue(mergeData, mergeKeyName, d.id);
                    if (id == null) return false;
                    return true;
                })).attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-borders state-border");

                map.append("path").datum(topojson.merge(mapData, mapData.objects.states.geometries.filter(function (d) {
                    var id = chartUtils.findValue(mergeData, mergeKeyName, d.id);
                    if (id == null) return true;
                    return false;
                }))).attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-region state merged").on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });
            }
        }

        if (mapType.toLowerCase() == "uscounties") {
            svg.attr('zoomFactor', zoom);
            if (mergeData == null) {
                map.append("g").selectAll("path").data(topojson.feature(mapData, mapData.objects.counties).features).enter().append("path").attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-region county dataviz").attr('coord', function (d) {
                    if (regionCrumbMetaName == null) return;

                    regionCrumbFormat.FieldName = regionCrumbFieldName;
                    regionCrumbFormat.Value = d.id;

                    return JSON.stringify(regionCrumbFormat);
                }).on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });

                map.append("path").datum(topojson.mesh(mapData, mapData.objects.counties, function (a, b) {
                    return a !== b;
                })).attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-borders county-border");
            } else {
                mergeData = chartUtils.getUnique(mergeData, mergeKeyName);

                map.append("g").selectAll("path").data(topojson.feature(mapData, mapData.objects.counties).features.filter(function (d) {
                    var id = d.id.toString();
                    if (mergeData.indexOf(id) < 0) return false;
                    return true;
                })).enter().append("path").attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-region county dataviz").attr('coord', function (d) {
                    if (regionCrumbMetaName == null) return;

                    regionCrumbFormat.FieldName = regionCrumbFieldName;
                    regionCrumbFormat.Value = d.id;

                    return JSON.stringify(regionCrumbFormat);
                }).on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });

                map.append("path").datum(topojson.mesh(mapData, mapData.objects.counties, function (d) {
                    var id = d.id.toString();
                    if (mergeData.indexOf(id) < 0) return false;
                    return true;
                })).attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-borders county-border");

                map.append("path").datum(topojson.merge(mapData, mapData.objects.counties.geometries.filter(function (d) {
                    var id = d.id.toString();
                    if (mergeData.indexOf(id) < 0) return true;
                    return false;
                }))).attr("d", path).attr('values', function (d) {
                    return JSON.stringify(d);
                }).attr("class", "geo-region county merged").on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });
            }
        }

        if (markerType != "pie") {
            var range = maxScale - minScale;

            var markerGroup = svg.append("g").attr("class", "map-markers");

            for (var i = 0; i < markers.length; i++) {
                markers[i]["scale"] = Number((markers[i].actualScale - minScale) / range);
            }markerGroup.selectAll('.marker').data(markers).enter().append('path').attr('transform', function (d) {
                if (mapViewType.toLowerCase() !== "standard") {
                    return "translate(" + leafletMap.latLngToLayerPoint([d.lat, d.long]).x + "," + leafletMap.latLngToLayerPoint([d.lat, d.long]).y + ")";
                }
            }).attr('d', d3.svg.symbol().type(function (d) {
                if (d.markerType != undefined || d.markerType != null) return d.markerType;
                return markerType;
            }).size(function (d) {
                var size = null;
                if (mapType.toLowerCase() == "world") size = +(scale / 2).toFixed(0);else size = +(scale / 16).toFixed(0);

                var sizeFactor = Number(d["scaleFactor"]);

                sizeFactor = sizeFactor <= 0 ? 1 : sizeFactor;

                if (sizeFactor > 0) size *= sizeFactor;

                if (d.scale == null || isNaN(d.scale)) return size;

                return size * d.scale;
            })).attr("size", function (d) {
                var size = null;
                if (mapType.toLowerCase() == "world") size = +(scale / 2).toFixed(0);else size = +(scale / 16).toFixed(0);

                var sizeFactor = Number(d["scaleFactor"]);

                sizeFactor = sizeFactor <= 0 ? 1 : sizeFactor;

                if (sizeFactor > 0) size *= sizeFactor;

                if (d.scale == null || isNaN(d.scale)) return size;

                return size * d.scale;
            }).attr('seriesidentifier', function (d) {
                return d.SeriesName;
            }).attr('marker', function (d) {
                if (d.markerType != undefined || d.markerType != null) return d.markerType;
                return markerType;
            }).attr('class', function (d) {
                if (d.class == null || d.class.trim().length == 0) return 'map-marker dataviz editviz';

                if (d.SeriesLengthCheck === 1) {
                    if (!d.UseColor && (d.ColorPalette && d.ColorPalette.length > 0 || d.ColorScheme && d.ColorScheme.length > 0)) return 'map-marker dataviz editviz ' + chartUtils.makeStyleName(d.id).toLowerCase();
                }

                return 'map-marker dataviz editviz ' + d.class.toLowerCase();
            }).attr('selector', function (d) {
                if (d.class == null || d.class.trim().length == 0) return '';

                return d.class.toLowerCase();
            }).attr('loc', function (d) {
                return JSON.stringify([d.long, d.lat]);
            }).attr('id', function (d) {
                return d.id;
            }).attr('locname', function (d) {
                return d.name;
            }).attr('label', function (d) {
                var item = d;
                if (item == null) return;

                if (d.tooltipText != null) {
                    return d.tooltipText;
                } else {
                    var defaultTip = "<span class='tip-header'>" + d.MarkerField + ": </span><span class='tip-value'> " + d[d.MarkerField] + "</span>";
                    return defaultTip;
                }
            }).attr('seriesname', function (d) {
                return d.SeriesName;
            }).attr('coord', function (d, i) {
                if (d.crumbMetaName == null) return;

                crumbFormat.Name = d.crumbMetaName;
                crumbFormat.FieldName = d.crumbFieldName;
                crumbFormat.Value = d[d.crumbFieldName];
                crumbFormat.DisplayFieldName = d.crumbDisplayFieldName;
                crumbFormat.DisplayValue = d[d.crumbDisplayFieldName];
                crumbFormat.DefaultPerspective = currentSeries.DefaultPerspective;

                return JSON.stringify(crumbFormat);
            }).style('fill', function (d, i) {
                if (d.color != null && d.color.trim().length > 0) return d.color;
                return '#f1e37b';
            });
        } else for (var i = 0; i < markers.length; i++) {
            drawPie(markers[i]);
        } //Draw connection lines
        var links = [];

        if (connectionsData != null) {
            var connectionConfig = {};
            connectionConfig.FromMarkerField = fromMarkerField;
            connectionConfig.ToMarkerField = toMarkerField;
            connectionConfig.ClassifierField = connectionClassifierField;

            buildConnectionsData(connectionsData, connectionConfig, markers, links);
        }

        //Include other connections
        if (connectionSeriesConfig != null && connectionSeriesConfig.length > 0 && connectionSeriesData != null) {
            for (var i = 0; i < connectionSeriesConfig.length; i++) {
                var currentSeries = connectionSeriesConfig[i];

                if (connectionSeriesData[currentSeries.SeriesName] == null) continue;

                var seriesData = JSON.parse(connectionSeriesData[currentSeries.SeriesName]);
                buildConnectionsData(seriesData, currentSeries, markers, links);
            }
        }

        if (links != null && links.length > 0) {
            var linkGroup = svg.append("g").attr("class", "map-link");

            linkGroup.selectAll(".map-link").data(links).enter().append("path").attr('class', function (d) {
                if (d["class"] != null || d["class"] != undefined) return 'map-link ' + d["class"].toLowerCase();
                return 'map-link';
            }).attr('coordinates', function (d) {
                return JSON.stringify(d.coordinates);
            }).attr({
                d: path
            }).style({
                'fill': 'none',
                'stroke-width': '2px'
            }).style('stroke', function (d) {
                return d.color;
            });
        }

        if (stampText != null && stampText.trim().length > 0) {
            var stampTextContainer = svg.append('g');

            var rectWidth = width / 1.5;
            var rectHeight = 80;

            stampTextContainer.append("foreignObject").attr("x", 0).attr("y", 0).attr("width", rectWidth).attr("height", rectHeight).append("xhtml:body").html("<p class='stampText'>" + stampText + "</p>");
        }

        function buildPieData(id, name) {
            var accumulator = [];

            for (var i = 0; i < data.length; i++) {
                var item = data[i];

                if (markerPieValueField != null) {
                    if (item[markerPieValueField] == null) continue;

                    if (item[markerIdFieldName] != id) continue;

                    var classifier = item[markerClassifierField];
                    var value = Number(item[markerPieValueField]);

                    if (classifier == null) continue;

                    var d = chartUtils.findValue(accumulator, "key", classifier);

                    if (d == null) accumulator.push({
                        key: classifier,
                        value: value,
                        name: name
                    });else d.value += value;
                }

                if (pieFieldNames != null) {
                    for (var j = 0; j < pieFieldNames.length; j++) {
                        var pieFieldName = pieFieldNames[j];

                        if (item[pieFieldName] == null) continue;

                        if (item[markerIdFieldName] != id) continue;

                        var classifier = pieFieldName;
                        var value = Number(item[pieFieldName]);

                        var d = chartUtils.findValue(accumulator, "key", classifier);

                        if (d == null) accumulator.push({
                            key: classifier,
                            value: value,
                            name: name
                        });else d.value += value;
                    }
                }
            }

            return accumulator;
        };

        function drawPie(data) {
            var loc = [data.long, data.lat];

            var coord = projection(loc);

            var pieContainer = svg.append("g").attr("class", "pie").attr("transform", "translate(" + coord[0] + "," + coord[1] + ")").attr("loc", JSON.stringify(loc));

            var color = d3.scale.category20b();
            color.domain(data.pieData.map(function (d) {
                return d.key;
            }));

            var arc = d3.svg.arc().outerRadius(pieRadius * 0.8).innerRadius(0);

            var outerArc = d3.svg.arc().innerRadius(pieRadius).outerRadius(pieRadius * 0.9);

            var key = function key(d) {
                return d.data.key;
            };

            function midAngle(d) {
                return d.startAngle + (d.endAngle - d.startAngle) / 2;
            }

            var pie = d3.layout.pie().sort(null).value(function (d) {
                return d.value;
            });

            var slice = pieContainer.selectAll(".slice").data(pie(data.pieData), key).enter().append("g").attr("class", "slice");

            slice.append("path").attr("d", function (d) {
                return arc(d);
            }).attr("fill", function (d, i) {
                return color(i);
            }).attr('exploded', '0').attr('startangle', function (d) {
                return d.startAngle;
            }).attr('endangle', function (d) {
                return d.endAngle;
            }).attr('id', function (d, i) {
                return 'Slice' + i;
            }).attr("class", function (d) {
                return d.data.key.toLowerCase();
            }).attr("label", function (d) {
                var item = d;
                if (item == null) return;

                if (d.tooltipText != null) {
                    return d.tooltipText;
                } else {
                    var defaultTip = "<span class='tip-header'>" + d.MarkerField + ": </span><span class='tip-value'> " + d[d.MarkerField] + "</span>";
                    return defaultTip;
                }
            }).attr('coord', function (d, i) {
                if (crumbFieldName != null) crumbFormat.Value = d[crumbFieldName];

                if (crumbDisplayFieldName != null) crumbFormat.DisplayValue = d[crumbDisplayFieldName];

                crumbFormat.DefaultPerspective = currentSeries.DefaultPerspective;
                return JSON.stringify(crumbFormat);
            });
        };

        function buildConnectionsData(data, config, markers, linkAccumulator) {
            if (data == null || data.length == 0 || config == null) return;

            if (linkAccumulator == null) linkAccumulator = [];

            var fromMarkerField = config.FromMarkerField;
            var toMarkerField = config.ToMarkerField;
            var connectionClassifierField = config.ClassifierField;

            if (fromMarkerField == null || toMarkerField == null) return;

            for (var i = 0; i < data.length; i++) {
                var item = data[i];

                if (item == null) continue;

                if (item[fromMarkerField] == null || item[fromMarkerField] == '' || item[toMarkerField] == null || item[toMarkerField] == '') continue;

                //look up fromMarker coordinates
                var fromMarker = chartUtils.findValue(markers, "id", item[fromMarkerField]);

                if (fromMarker == null) continue;

                //look up toMarker coordinates
                var toMarker = chartUtils.findValue(markers, "id", item[toMarkerField]);

                if (toMarker == null) continue;

                var link = {};
                link.type = "LineString";
                link.coordinates = [[fromMarker.long, fromMarker.lat], [toMarker.long, toMarker.lat]];

                if (connectionClassifierField != null && item[connectionClassifierField] != null) link["class"] = item[connectionClassifierField];

                link.color = fromMarker.color;
                linkAccumulator.push(link);
            }

            return linkAccumulator;
        }

        function convertHexToRGB(color) {
            var r, g, b;

            if (color == null) return;

            if (color.charAt(0) == '#') {
                color = color.substr(1);
            }
            if (color.length == 3) {
                color = color.substr(0, 1) + color.substr(0, 1) + color.substr(1, 2) + color.substr(1, 2) + color.substr(2, 3) + color.substr(2, 3);
            }
            r = color.charAt(0) + '' + color.charAt(1);
            g = color.charAt(2) + '' + color.charAt(3);
            b = color.charAt(4) + '' + color.charAt(5);
            r = parseInt(r, 16);
            g = parseInt(g, 16);
            b = parseInt(b, 16);
            return r + ', ' + g + ', ' + b;
        };

        if (showLegend) {
            var legendItem = {};
            var legendItems = [];

            if (markerClassifiers.length > 0) markerClassifiers.forEach(function (c, i) {
                legendItem = {};
                legendItem["text"] = c;
                legendItem["type"] = markerType;

                legendItems.push(legendItem);
            });

            if (markerSeriesConfig != null && markerSeriesConfig.length > 0 && markerSeriesConfig.length < 2 && !markerSeriesConfig[0].UseColor) markerSeriesConfig.forEach(function (markerSeries, i) {
                if (markerSeries.ColorPalette && markerSeries.ColorPalette.length > 0 && !markerSeries.UseColor || colorScheme && colorScheme.length > 0 && !markerSeries.UseColor) for (var item in markerSeries["itemToColorMapping"]) {
                    legendItem = {};
                    legendItem["text"] = item;
                    legendItem["color"] = markerSeries["itemToColorMapping"][item];
                    legendItem["type"] = getMarkerType(markerSeries.MarkerType);
                    legendItems.push(legendItem);
                }
            });else if (markerSeriesConfig != null && markerSeriesConfig.length > 0) markerSeriesConfig.forEach(function (n, i) {
                legendItem = {};
                legendItem["text"] = n.MarkerClassifierFieldName == null || n.MarkerClassifierFieldName.trim().length == 0 ? n.SeriesName : n.MarkerClassifierFieldName;
                legendItem["type"] = getMarkerType(n.MarkerType);
                legendItem["color"] = n.Color;
                legendItems.push(legendItem);
            });

            var legendCount = legendItems.length;
            var legendSectionWidth = width / legendCount;

            var vert = height * 0.8;
            var verticalPadding = 0.5 * legendRectSize;

            var m = Math.round(legendCount / 2);

            for (var i = 0; i < legendCount; i++) {
                if (i != 0) {
                    if (i % 2 == 1) m = m + i;else m = m - i;
                }

                var legend = svg.append("g").attr('transform', function (d) {
                    return 'translate(' + Number(width * 0.1) + ',' + Number(vert - i * (legendRectSize + verticalPadding)) + ')';
                }).attr('class', 'map-legend-container');

                legend.append('path').attr('d', d3.svg.symbol().type(function (d) {
                    var type = legendItems[i].type;

                    if (type != undefined || type != null) return type;

                    return markerType;
                }).size(function (d) {
                    return legendRectSize * 5;
                })).style('fill', function (d) {
                    var color = legendItems[i].color;
                    if (color != null && color.trim().length > 0) return color;
                }).attr('class', function () {
                    return 'map-legend legend ' + chartUtils.makeStyleName(legendItems[i].text);
                }).attr("type", function (d) {
                    var type = legendItems[i].type;

                    if (type != undefined || type != null) return type;

                    return markerType;
                }).attr("marker-class-name", chartUtils.makeStyleName(legendItems[i].text)).on("click", function () {
                    var selector = "#" + parentContainerId + " .map-marker." + $(this).attr("marker-class-name");

                    if (!d3.select(this).classed("inactive")) {
                        d3.select(this).classed("inactive", true);

                        if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", true);

                        if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", true);

                        d3.selectAll(selector).transition().duration(200).attr('opacity', 0).each("end", function (d) {
                            d3.select(this).style("display", "none");
                        });
                    } else {
                        d3.select(this).classed("inactive", false);

                        if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", false);

                        if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", false);

                        d3.selectAll(selector).style("display", "");
                        d3.selectAll(selector).transition().duration(200).attr('opacity', 1);
                    }
                });

                var text = legend.append('text').attr('x', legendRectSize).attr('class', 'map-legend-text legend-text ' + chartUtils.makeStyleName(legendItems[i].text)).style('text-anchor', 'start').style('dominant-baseline', 'central').style("fill", "white").text(function () {
                    return legendItems[i].text;
                }).attr("marker-class-name", chartUtils.makeStyleName(legendItems[i].text)).on("click", function () {
                    var selector = "#" + parentContainerId + " .map-marker." + $(this).attr("marker-class-name");

                    if (!d3.select(this).classed("inactive")) {
                        d3.select(this).classed("inactive", true);

                        if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", true);

                        if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", true);

                        d3.selectAll(selector).transition().duration(200).attr('opacity', 0).each("end", function (d) {
                            d3.select(this).style("display", "none");
                        });
                    } else {
                        d3.select(this).classed("inactive", false);

                        if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", false);

                        if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", false);

                        d3.selectAll(selector).style("display", "");
                        d3.selectAll(selector).transition().duration(200).attr('opacity', 1);
                    }
                });
            }
        }

        //Include heat maps
        var heatmapCenter = null;
        var heatMapZoomFactor = null;
        if (heatMapSeriesConfig != null && heatMapSeriesConfig.length > 0 && heatMapSeriesData != null) {

            var heatmapSeriesColorScale = d3.scale.category20();

            if (colorScheme != null && colorScheme.length > 0) heatmapSeriesColorScale = d3.scale.ordinal().range(colorScheme);

            for (var i = 0; i < heatMapSeriesConfig.length; i++) {
                var currentSeries = heatMapSeriesConfig[i];

                if (heatMapSeriesData[currentSeries.SeriesName] == null) continue;

                var seriesData = JSON.parse(heatMapSeriesData[currentSeries.SeriesName]);

                if (seriesData == null || seriesData.length == 0) continue;

                var heatMapAccumulator = [];
                var minValue = 0;
                var maxValue = 0;
                var range = 0;
                var baseRGB = currentSeries.BaseRGB;
                var colorRange = currentSeries.ColorRange;
                var domain = currentSeries.ColorDomain;
                var referenceField = currentSeries.ReferenceField;
                var valueField = currentSeries.ValueField;
                var levelClassifier = currentSeries.LevelClassifier;
                var aggregationType = currentSeries.AggregationType;
                var toolTipField = currentSeries.TipField;
                var crumbField = currentSeries.CrumbFieldName;
                var crumbMeta = currentSeries.CrumbMetaName;
                var crumbDisplayField = currentSeries.CrumbDisplayFieldName;
                var heatmapSeriesName = chartUtils.replaceAll(currentSeries.SeriesName, ' ', '');

                if (toolTipField == null && tipField != null) toolTipField = tipField;

                if (baseRGB != null && baseRGB.substr(0, 1) == "#") baseRGB = chartUtils.convertHexToRGB(baseRGB);

                //Default color range
                if (colorRange == null) {
                    colorRange = [];
                    colorRange.push("#FFFFFF");
                    colorRange.push(heatmapSeriesColorScale(heatmapSeriesName));
                }

                if (mapType.toLowerCase() == "usstates") levelClassifier = "state";else if (mapType.toLowerCase() == "uscounties") levelClassifier = "county";else levelClassifier = "geo-region";

                if (levelClassifier == null) continue;

                var heatMapRegion = map.selectAll('.' + levelClassifier);

                if (heatMapRegion == null || heatMapRegion.length == 0) continue;

                var tooltipConfigs = null;
                var tooltipText = null;

                if (currentSeries.CompoundTipField != null) {
                    tooltipConfigs = currentSeries.CompoundTipField.TooltipConfigs;

                    tooltipText = currentSeries.CompoundTipField.TooltipText;
                }

                maxValue = d3.max(seriesData, function (d) {
                    return Number(d[valueField]);
                });

                minValue = d3.min(seriesData, function (d) {
                    return Number(d[valueField]);
                });

                var colorDomain = [];

                if (domain && domain.length == colorRange.length) for (var k = 0; k < domain.length; k++) {
                    colorDomain.push((maxValue - minValue) * Number(domain[k]));
                } else for (var k = 0; k < colorRange.length; k++) {
                    colorDomain.push((maxValue - minValue) / (colorRange.length - 1) * k);
                }var color = d3.scale.linear().domain([minValue, maxValue]).range(colorRange);

                if (colorRange.length > 2) {
                    var medianValue = d3.median(seriesData, function (d) {
                        return Number(d[valueField]);
                    });
                    color = d3.scale.linear().domain([minValue, medianValue, maxValue]).range(colorRange);
                }

                range = maxValue - minValue;

                for (var j = 0; j < seriesData.length; j++) {
                    var item = seriesData[j];

                    if (item == null || item[referenceField] == null) continue;

                    var value = {};
                    value[referenceField] = item[referenceField];

                    value['counter'] = item["RecordCount"];

                    value[valueField] = Math.round(Number(item[valueField]) * 100) / 100;

                    if (item[toolTipField] != null) value[toolTipField] = item[toolTipField];

                    if (item[crumbField] != null) {
                        value["crumbMetaName"] = crumbMeta;
                        value["crumbFieldName"] = crumbField;
                        value[crumbField] = item[crumbField];
                        value["valueFieldName"] = valueField;
                        value["DisplayValue"] = value[valueField];
                        value["SeriesName"] = currentSeries.SeriesName;

                        if (item[crumbDisplayField] != null) {
                            value["crumbDisplayFieldName"] = crumbDisplayField;
                            value[crumbDisplayField] = item[crumbDisplayField];
                        }
                    }

                    if (currentSeries.ZoneClassifierName != null && item[currentSeries.ZoneClassifierName] != null) {
                        var zoneId = item[currentSeries.ZoneClassifierName];

                        var maxColor = heatmapSeriesColorScale(zoneId);
                        var minColor = d3.rgb(maxColor).brighter(0.6);

                        var tempRange = [minColor, maxColor];

                        value["color"] = d3.scale.linear().domain([minValue, maxValue]).range(tempRange);
                    }

                    if (tooltipConfigs != null) {
                        for (var tipIndex = 0; tipIndex < tooltipConfigs.length; tipIndex++) {
                            var tipItem = tooltipConfigs[tipIndex];

                            value[tipItem.Id] = chartUtils.getTooltipValue(tipItem, item);

                            if (tipItem.TipField == currentSeries.ZoneClassifierName && item[currentSeries.ZoneClassifierName] != null) value[tipItem.Id] = item[currentSeries.ZoneClassifierName];

                            if (tipItem.TipField == crumbField && item[crumbField] != null) value[tipItem.Id] = item[crumbField];

                            if (tipItem.TipField == crumbDisplayField && item[crumbDisplayField] != null) value[tipItem.Id] = item[crumbDisplayField];
                        }
                    }

                    heatMapAccumulator.push(value);
                }

                heatMapRegion.style("fill", function (d) {
                    var item = chartUtils.findValue(heatMapAccumulator, referenceField, d.id);

                    if (item == null) return;

                    if (item[valueField] == null) return;

                    if (calcCenterCoords || setAutoZoom) {
                        var bounds = path.bounds(d);
                        getMinMaxLatLong(bounds);
                    }

                    var tempColor = color;

                    if (item["color"] != null) tempColor = item["color"];

                    return tempColor(Number(item[valueField]));
                }).attr('label', function (d) {
                    var item = chartUtils.findValue(heatMapAccumulator, referenceField, d.id);

                    if (item == null) return;

                    var tipHeader = currentSeries.SeriesName;

                    if (item[toolTipField] != null) tipHeader = item[toolTipField] + "<br /> " + tipHeader;

                    var defaultTipText = "<span class='tip-header'>" + tipHeader + ":</span><span class='tip-value'> " + chartUtils.format(item[valueField], currentSeries.DisplayFormat, currentSeries.CurrencyPrefix, null, currentSeries.BaseFormat) + "</span>";

                    var tooltip = currentSeries.CompoundTipField;

                    var tipText = chartUtils.buildTooltipText(tooltip, item, defaultTipText, valueField, currentSeries.AggregationType, currentSeries.TotalRecords);

                    return tipText;
                }).attr('seriesidentifier', function (d) {
                    return currentSeries.SeriesName;
                }).attr('seriesname', function (d) {
                    return d.SeriesName;
                }).attr('coord', function (d) {
                    var item = chartUtils.findValue(heatMapAccumulator, referenceField, d.id);

                    if (item == null) return;

                    if (currentSeries.CrumbMetaName == null) return;

                    crumbFormat.Name = item["crumbMetaName"];
                    crumbFormat.FieldName = item["crumbFieldName"];
                    crumbFormat.Value = item[crumbFormat.FieldName];
                    crumbFormat.DisplayFieldName = item["crumbDisplayFieldName"];
                    crumbFormat.DisplayValue = item[crumbFormat.DisplayFieldName];
                    crumbFormat.DefaultPerspective = currentSeries.DefaultPerspective;

                    return JSON.stringify(crumbFormat);
                });

                if (currentSeries.ShowLegend) {
                    var count = 20;
                    var colorIndex = 100;
                    var vert = height * 0.97;

                    var xPos = width * 0.02;
                    var yPos = vert - legendRectSize;
                    var labelYPos = yPos - height * 0.01;

                    svg.append('text').attr('x', xPos).attr('y', labelYPos).attr('class', 'legend-text heatmap-legend-text').style('text-anchor', 'start').style("fill", "#000").text(chartUtils.format(minValue, currentSeries.DisplayFormat, currentSeries.CurrencyPrefix, null, currentSeries.BaseFormat));

                    for (var k = minValue; k < maxValue; k = k + (maxValue - minValue) / count) {
                        svg.append('rect').attr('x', xPos).attr('y', yPos).attr('width', legendRectSize).attr('height', legendRectSize).attr('class', 'legend heatmap-legend').style('fill', color(k));

                        xPos += legendRectSize;
                        colorIndex -= 8;
                    }

                    svg.append('text').attr('x', xPos).attr('y', labelYPos).attr('class', 'legend-text heatmap-legend-text').style('text-anchor', 'end').style("fill", "#000").text(chartUtils.format(maxValue, currentSeries.DisplayFormat, currentSeries.CurrencyPrefix, null, currentSeries.BaseFormat));
                }
            }
        }

        if (calcCenterCoords) {
            var centerLatitude = Number(((Number(maxLatitude) + Number(minLatitude)) / 2).toFixed(4));
            var centerLongitude = Number(((Number(maxLongitude) + Number(minLongitude)) / 2).toFixed(4));
            centerCoordinates = [centerLatitude, centerLongitude];
        }

        if (setAutoZoom) {
            var x1y1 = projection([minLongitude, maxLatitude]);
            var x2y2 = projection([maxLongitude, minLatitude]);
            var zoomWidth = Math.abs(Number(x2y2[0]) - Number(x1y1[0]));
            var zoomHeight = Math.abs(Number(x1y1[1]) - Number(x2y2[1]));

            if (zoomWidth == 0 && zoomHeight == 0) {
                zoomFactor = 1;
            } else if (zoomWidth == 0) {
                zoomFactor = zoomMargin / (zoomHeight / height);
            } else if (zoomHeight == 0) {
                zoomFactor = zoomMargin / (zoomWidth / width);
            } else {
                zoomFactor = zoomMargin / Math.max(zoomHeight / height, zoomWidth / width);
            }
        }
        var zoomLevel = Math.min(maximumZoomScale, zoomFactor * scale);

        if (mapType.toLowerCase() == "usstates" || mapType.toLocaleLowerCase() == "uscounties") {
            svg.attr('scale', zoomLevel);
            svg.attr('centerLocation', JSON.stringify(centerCoordinates));
            redrawMap();
        }

        if (mapViewType != null && mapViewType.toLowerCase() != "standard") {
            if (mapType != null && (mapType == 3 || mapType.toString().toLowerCase() == "uscounties" || mapType.toString().toLowerCase() == "usstates" || mapType == 2 || mapType.toString().toLowerCase() == "catdealers")) {
                redrawEnhancedUSMap(parentContainerId);
                return;
            } else {
                redrawEnhancedWorldMap(parentContainerId);
            }
        }

        function loadEnhancedUSMap(containerId) {
            var config = $("#" + containerId).parent().attr("value");
            config = decodeURIComponent(config);
            var widgetConfig = JSON.parse(config);
            var mapConfig = widgetConfig.MapConfig;

            var markers = $("#" + containerId).find(".map-marker");
            var oldsvg = d3.select("#" + containerId).select("svg");
            var legendContainer = d3.select("#" + containerId).selectAll(".map-legend-container");

            var width = Number(oldsvg.attr("width"));
            var height = Number(oldsvg.attr("height"));
            var scale = Number(oldsvg.attr("scale"));
            var zoom = Number(oldsvg.attr("zoomFactor"));
            var markerType = oldsvg.attr("marktype");
            var pieRadius = oldsvg.attr("radius");
            var pieInnerRadius = oldsvg.attr("innerradius");
            var centerCoordinates = JSON.parse(oldsvg.attr("centerLocation"));
            var mapViewType = oldsvg.attr("mapviewtype");
            var leafletMap = null;
            var setAutoZoom = true;

            $("#" + containerId).find(".leaflet-pane").remove();
            $("#" + containerId).find(".leaflet-control-container").remove();

            var leafletContainer = $("<div></div>");
            var leafletContainerId = "leaflet_" + containerId;
            leafletContainer.attr("id", leafletContainerId);
            leafletContainer.width(width);
            leafletContainer.height(height);

            if ($("#" + containerId).find("div.front").length > 0) $("#" + containerId + " div.front").append(leafletContainer);else $("#" + containerId).append(leafletContainer);

            //Restrict panning outside map
            var southWest = L.latLng(-89.98155760646617, -180),
                northEast = L.latLng(89.99346179538875, 180);
            var bounds = L.latLngBounds(southWest, northEast);

            if (leafletMap == null || leafletMap.getContainer().id != containerId) leafletMap = L.map(leafletContainerId, {
                minZoom: leafletMinZoom, maxZoom: leafletMaxZoom,
                zoomControl: false,
                attributionControl: false,
                worldCopyJump: true,
                //zoomSnap: 0,
                zoomDelta: 0.1,
                doubleClickZoom: false,
                paddingTopLeft: [15, 15],
                maxBounds: bounds,
                maxBoundsViscosity: 1.0
            });

            leafletMap.setView(centerCoordinates, Math.max(zoom, leafletMinZoom));

            if (zoom > 1) setAutoZoom = false;

            if (mapConfig.OverlayConfigs != null) {

                var imageOverlays = L.layerGroup();

                mapConfig.OverlayConfigs.forEach(function (overlayConfig) {
                    var url = window.location.href + overlayConfig.OverlayImage;

                    if (overlayConfig.FileToken != null) url = tadaBaseUrl + "/files?accesskey=" + accessKey + "&fileToken=" + overlayConfig.FileToken;

                    L.imageOverlay(url, [overlayConfig.SouthEastCoordinates, overlayConfig.NorthWestCoordinates]).addTo(imageOverlays);
                });

                imageOverlays.addTo(leafletMap);
            }

            var layer = L.gridLayer.googleMutant({
                maxZoom: leafletMaxZoom,
                type: mapViewType.toLowerCase(),
                labels: true
            }).addTo(leafletMap);

            var svgLayer = L.svg({ padding: 0 });
            svgLayer.addTo(leafletMap);

            if (mapConfig.MarkerSeries != null) {
                var update = function update() {
                    markers.attr("transform", function (d) {
                        var latLong = JSON.parse($(d).attr("loc"));
                        return "translate(" + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).x + "," + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).y + ")";
                    }).attr("d", function (d) {
                        var m = d3.select(this);

                        var path = d3.svg.symbol().type(m.attr("marker")).size(function (d) {
                            var markerSize = m.attr("size");

                            var size = +(scale / 2).toFixed(0);

                            if (markerSize == null || isNaN(markerSize)) return size;

                            return markerSize;
                        });

                        return path();
                    });
                    centerCoordinates = [];
                    centerCoordinates.push(Number(leafletMap.getCenter()["lat"].toFixed(4)));
                    centerCoordinates.push(Number(leafletMap.getCenter()["lng"].toFixed(4)));
                };

                var panned = function panned() {
                    centerCoordinates = [];
                    centerCoordinates.push(Number(leafletMap.getCenter()["lat"].toFixed(4)));
                    centerCoordinates.push(Number(leafletMap.getCenter()["lng"].toFixed(4)));
                };

                var minLat = 90;
                var maxLat = -90;
                var minLon = 180;
                var maxLon = -180;
                var svg = d3.select(leafletMap.getPanes().overlayPane).select("svg");
                svg.attr("scale", scale);
                svg.attr("zoomfactor", zoom);
                svg.attr("marktype", markerType);
                svg.attr("radius", pieRadius);
                svg.attr("pieInnerRadius", pieInnerRadius);
                svg.attr("centerlocation", centerCoordinates);

                var markerGroup = svg.select("g").attr("class", "leaflet-zoom-hide");

                var markers = markerGroup.selectAll('.marker').data(markers).enter().append('path').attr('transform', function (d) {
                    var latLong = JSON.parse($(d).attr("loc"));
                    if (setAutoZoom) {
                        minLat = Math.min(minLat, latLong[1]);
                        maxLat = Math.max(maxLat, latLong[1]);
                        minLon = Math.min(minLon, latLong[0]);
                        maxLon = Math.max(maxLon, latLong[0]);
                    }
                    return "translate(" + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).x + "," + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).y + ")";
                }).attr('d', function (d) {
                    return $(d).attr("d");
                }).attr("seriesidentifier", function (d) {
                    return $(d).attr("seriesidentifier");
                }).attr("size", function (d) {
                    return $(d).attr("size");
                }).attr('marker', function (d) {
                    return $(d).attr("marker");
                }).attr('class', function (d) {
                    return $(d).attr("class");
                }).attr('selector', function (d) {
                    return $(d).attr("selector");
                }).attr('loc', function (d) {
                    return $(d).attr("loc");
                }).attr('id', function (d) {
                    return $(d).attr("id");
                }).attr('locname', function (d) {
                    return $(d).attr("locname");
                }).attr('label', function (d) {
                    return $(d).attr("label");
                }).attr('coord', function (d, i) {
                    return $(d).attr("coord");
                }).attr('seriesname', function (d, i) {
                    return $(d).attr("seriesname");
                }).style('fill', function (d, i) {
                    return $(d).css("fill");
                }).on("touchstart", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    usMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", usMapTooltip.onMouseOut).on("touchmove", usMapTooltip.onTouchMove).on("mousemove", function () {
                    usMapTooltip.onMouseMove.call(this, parentContainerId);
                });

                //Set auto zoom only if user has not specified zoom.
                if (setAutoZoom) leafletMap.flyToBounds([[minLat, minLon], [maxLat, maxLon]], { duration: 0.01 });

                //Custom leaflet zoom control
                $("#" + leafletContainerId).append($(leafletZoomDiv).clone());

                //Append legend
                if (legendContainer != null && legendContainer[0][0] != null) {
                    var $customLegend = $(leafletLegendDiv).clone();

                    $("#" + leafletContainerId).append($customLegend);

                    var legendScale = Math.min($customLegend.width(), $customLegend.height());
                    var legendRectSize = 0.2 * legendScale;
                    var vert = $customLegend.height() * 0.5;
                    var verticalPadding = 0.2 * $customLegend.height();
                    var svgHeight = (legendRectSize + verticalPadding * 2) * legendContainer[0].length;

                    var legendSVG = d3.select("#" + leafletContainerId + " .leaflet-control-custom-legend .legend-svg").append('svg').attr("width", $customLegend.width() * 0.8).attr("height", svgHeight);

                    var m = Math.round(legendContainer[0].length / 2);

                    for (var i = 0; i < legendContainer[0].length; i++) {
                        if (i != 0) {
                            if (i % 2 == 1) m = m + i;else m = m - i;
                        }

                        var legendItem = $(legendContainer[0][i]);
                        var legendPath = $(legendItem.find("path")[0]);
                        var legendText = $(legendItem.find("text")[0]);

                        var lineCount = 1;
                        if (legendText.text().length > 10) lineCount = legendText.text().length / 10;

                        var legend = legendSVG.append("g").attr('transform', function (d) {
                            return 'translate(' + Number($customLegend.width() * 0.1) + ',' + Number(verticalPadding + i * (legendRectSize + verticalPadding)) + ')';
                        }).attr('class', 'map-legend-container');

                        legend.append('path').attr('d', d3.svg.symbol().type(function (d) {
                            var type = legendPath.attr("type");

                            if (type != undefined || type != null) return type;

                            return markerType;
                        }).size(function (d) {
                            return legendRectSize * 5;
                        })).style('fill', function (d) {
                            var color = legendPath.css("fill");
                            if (color != null && color.trim().length > 0) return color;
                        }).attr('class', function () {
                            return legendPath.attr("class");
                        }).attr("type", function (d) {
                            var type = legendPath.attr("type");

                            if (type != undefined || type != null) return type;

                            return markerType;
                        }).attr("marker-class-name", legendPath.attr("marker-class-name")).on("click", function () {
                            var selector = "#" + parentContainerId + " .map-marker." + $(this).attr("marker-class-name");

                            if (!d3.select(this).classed("inactive")) {
                                d3.select(this).classed("inactive", true);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", true);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", true);

                                d3.selectAll(selector).transition().duration(200).attr('opacity', 0).each("end", function (d) {
                                    d3.select(this).style("display", "none");
                                });
                            } else {
                                d3.select(this).classed("inactive", false);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", false);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", false);

                                d3.selectAll(selector).style("display", "");
                                d3.selectAll(selector).transition().duration(200).attr('opacity', 1);
                            }
                        });

                        var text = legend.append('text').attr('x', legendRectSize).attr('class', legendText.attr("class")).style('text-anchor', 'start').style('dominant-baseline', 'central').style('word-wrap', 'pre-wrap').text(function () {
                            return legendText.text();
                        }).attr("marker-class-name", legendText.attr("marker-class-name")).on("click", function () {
                            var selector = "#" + parentContainerId + " .map-marker." + $(this).attr("marker-class-name");

                            if (!d3.select(this).classed("inactive")) {
                                d3.select(this).classed("inactive", true);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", true);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", true);

                                d3.selectAll(selector).transition().duration(200).attr('opacity', 0).each("end", function (d) {
                                    d3.select(this).style("display", "none");
                                });
                            } else {
                                d3.select(this).classed("inactive", false);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", false);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", false);

                                d3.selectAll(selector).style("display", "");
                                d3.selectAll(selector).transition().duration(200).attr('opacity', 1);
                            }
                        });
                    }

                    legendSVG.attr("width", $customLegend.width() * 1.8);

                    $("#" + leafletContainerId + ".leaflet-control-custom-legend .legend-svg").resize(function () {
                        var container = $(this).parents(".leaflet-control-custom-legend");

                        container.width($(this).width());
                    });

                    $(".leaflet-control-custom-legend").on("mouseover", function () {
                        leafletMap.dragging.disable();
                        leafletMap.scrollWheelZoom.disable();
                    });
                    $(".leaflet-control-custom-legend").on("mouseout", function () {
                        leafletMap.dragging.enable();
                        leafletMap.scrollWheelZoom.enable();
                    });
                }

                leafletMap.on("moveend", update);

                if ($("#" + containerId).is(".edit-widget-container")) $("#" + leafletContainerId).append($(mapCenterDiv).clone());

                update();

                $(document).on("click", ".map-center", function (e) {
                    if (currentWidgetConfig != null && $("#CenterCoordinates-" + currentWidgetConfig.Id) != null && centerCoordinates != null) $("#CenterCoordinates-" + currentWidgetConfig.Id).val(JSON.stringify(centerCoordinates));
                });

                //leaflet allows only one zoom control per page by default.
                //Added custom zoom events to support multiple zoom controls
                // in single page.
                $(document).on("click", ".leaflet-control-zoom-in", function (e) {
                    if ($(this).parents(".leaflet-container").attr("id") != leafletMap.getContainer().id) return;
                    leafletMap.setZoom(leafletMap.getZoom() + 1);
                    e.preventDefault();
                });

                $(document).on("click", ".leaflet-control-zoom-out", function (e) {
                    if ($(this).parents(".leaflet-container").attr("id") != leafletMap.getContainer().id) return;
                    leafletMap.setZoom(leafletMap.getZoom() - 1);
                    e.preventDefault();
                });
            }
        }

        function redrawEnhancedUSMap(containerId) {
            var oldsvg = d3.select("#" + containerId).select("svg");

            if (oldsvg == null || oldsvg.length == 0 || oldsvg[0] == null || oldsvg[0][0] == null) return;

            var mapAccessToken = oldsvg.attr("mapAccessToken");

            if (window.google == null && mapAccessToken != null) {
                loadScript(googleMapsAPIUrl + "?key=" + mapAccessToken, function () {
                    loadEnhancedUSMap(containerId);
                });
            } else {
                loadEnhancedUSMap(containerId);
            }
        }

        function redrawEnhancedWorldMap(containerId) {
            var oldsvg = d3.select("#" + containerId).select("svg");

            if (oldsvg == null || oldsvg.length == 0 || oldsvg[0] == null || oldsvg[0][0] == null) return;

            var mapAccessToken = oldsvg.attr("mapAccessToken");

            if (window.google == null && mapAccessToken != null) {
                loadScript(googleMapsAPIUrl + "?key=" + mapAccessToken, function () {
                    loadEnhancedWorldMap(containerId);
                });
            } else {
                loadEnhancedWorldMap(containerId);
            }
        }

        function loadScript(url, callback) {
            //Adding the script tag to the head as suggested before.
            var head = document.getElementsByTagName('head')[0];
            var script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = url;

            //Then bind the event to the callback function.
            //There are several events for cross browser compatibility.
            script.onreadystatechange = callback;
            script.onload = callback;
            script.async = false;
            //Fire the loading.
            head.appendChild(script);
        }

        function loadEnhancedWorldMap(containerId) {
            var config = $("#" + containerId).parent().attr("value");
            config = decodeURIComponent(config);
            var widgetConfig = JSON.parse(config);
            var mapConfig = widgetConfig.MapConfig;

            var markers = $("#" + containerId).find(".map-marker");
            var oldsvg = d3.select("#" + containerId).select("svg");
            var legendContainer = d3.select("#" + containerId).selectAll(".map-legend-container");
            var width = Number(oldsvg.attr("width"));
            var height = Number(oldsvg.attr("height"));
            var scale = Number(oldsvg.attr("scale"));
            var zoom = Number(oldsvg.attr("zoomFactor"));
            var markerType = oldsvg.attr("marktype");
            var pieRadius = oldsvg.attr("radius");
            var pieInnerRadius = oldsvg.attr("innerradius");
            var centerCoordinates = JSON.parse(oldsvg.attr("centerLocation"));
            var mapViewType = oldsvg.attr("mapviewtype");
            var leafletMap = null;
            var setAutoZoom = true;

            $("#" + containerId).find(".leaflet-pane").remove();
            $("#" + containerId).find(".leaflet-control-container").remove();

            var leafletContainer = $("<div></div>");
            var leafletContainerId = "leaflet_" + containerId;
            leafletContainer.attr("id", leafletContainerId);
            leafletContainer.width(width);
            leafletContainer.height(height);

            if ($("#" + containerId).find("div.front").length > 0) $("#" + containerId + " div.front").append(leafletContainer);else $("#" + containerId).append(leafletContainer);

            //Restrict panning outside map
            var southWest = L.latLng(-89.98155760646617, -180),
                northEast = L.latLng(89.99346179538875, 180);
            var bounds = L.latLngBounds(southWest, northEast);

            if (leafletMap == null || leafletMap.getContainer().id != containerId) leafletMap = L.map(leafletContainerId, {
                minZoom: leafletMinZoom, maxZoom: leafletMaxZoom,
                zoomControl: false,
                attributionControl: false,
                worldCopyJump: true,
                //zoomSnap: 0.15,
                zoomDelta: 0.1,
                doubleClickZoom: false,
                paddingTopLeft: [15, 15],
                maxBounds: bounds,
                maxBoundsViscosity: 1.0
            });

            leafletMap.setView(centerCoordinates, Math.max(zoom, 2));

            if (zoom > 1) setAutoZoom = false;

            if (mapConfig.OverlayConfigs != null) {

                var imageOverlays = L.layerGroup();

                mapConfig.OverlayConfigs.forEach(function (overlayConfig) {
                    var url = window.location.href + overlayConfig.OverlayImage;

                    if (overlayConfig.FileToken != null) url = tadaBaseUrl + "/files?accesskey=" + accessKey + "&fileToken=" + overlayConfig.FileToken;

                    L.imageOverlay(url, [overlayConfig.SouthEastCoordinates, overlayConfig.NorthWestCoordinates]).addTo(imageOverlays);
                });

                imageOverlays.addTo(leafletMap);
            }

            var layer = L.gridLayer.googleMutant({
                maxZoom: leafletMaxZoom,
                type: mapViewType.toLowerCase(),
                labels: true
            }).addTo(leafletMap);

            var svgLayer = L.svg({ padding: 0 });
            svgLayer.addTo(leafletMap);

            if (mapConfig.MarkerSeries != null) {
                var update = function update() {
                    markers.attr("transform", function (d) {
                        var latLong = JSON.parse($(d).attr("loc"));
                        return "translate(" + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).x + "," + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).y + ")";
                    }).attr("d", function (d) {
                        var m = d3.select(this);

                        var path = d3.svg.symbol().type(m.attr("marker")).size(function (d) {
                            var markerSize = m.attr("size");

                            var size = +(scale / 2).toFixed(0);

                            if (markerSize == null || isNaN(markerSize)) return size;

                            return markerSize;
                        });

                        return path();
                    });
                    centerCoordinates = [];
                    centerCoordinates.push(Number(leafletMap.getCenter()["lat"].toFixed(4)));
                    centerCoordinates.push(Number(leafletMap.getCenter()["lng"].toFixed(4)));
                };

                var panned = function panned() {
                    centerCoordinates = [];
                    centerCoordinates.push(Number(leafletMap.getCenter()["lat"].toFixed(4)));
                    centerCoordinates.push(Number(leafletMap.getCenter()["lng"].toFixed(4)));
                };

                var minLat = 90;
                var maxLat = -90;
                var minLon = 180;
                var maxLon = -180;
                var svg = d3.select(leafletMap.getPanes().overlayPane).select("svg");
                svg.attr("scale", scale);
                svg.attr("zoomfactor", zoom);
                svg.attr("marktype", markerType);
                svg.attr("radius", pieRadius);
                svg.attr("pieInnerRadius", pieInnerRadius);
                svg.attr("centerlocation", centerCoordinates);

                var markerGroup = svg.select("g").attr("class", "leaflet-zoom-hide");

                var markers = markerGroup.selectAll('.marker').data(markers).enter().append('path').attr('transform', function (d) {
                    var latLong = JSON.parse($(d).attr("loc"));
                    if (setAutoZoom) {
                        minLat = Math.min(minLat, latLong[1]);
                        maxLat = Math.max(maxLat, latLong[1]);
                        minLon = Math.min(minLon, latLong[0]);
                        maxLon = Math.max(maxLon, latLong[0]);
                    }
                    return "translate(" + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).x + "," + leafletMap.latLngToLayerPoint([latLong[1], latLong[0]]).y + ")";
                }).attr('d', function (d) {
                    return $(d).attr("d");
                }).attr("size", function (d) {
                    return $(d).attr("size");
                }).attr("seriesidentifier", function (d) {
                    return $(d).attr("seriesidentifier");
                }).attr('marker', function (d) {
                    return $(d).attr("marker");
                }).attr('class', function (d) {
                    return $(d).attr("class");
                }).attr('selector', function (d) {
                    return $(d).attr("selector");
                }).attr('loc', function (d) {
                    return $(d).attr("loc");
                }).attr('id', function (d) {
                    return $(d).attr("id");
                }).attr('locname', function (d) {
                    return $(d).attr("locname");
                }).attr('label', function (d) {
                    return $(d).attr("label");
                }).attr('coord', function (d, i) {
                    return $(d).attr("coord");
                }).attr('seriesname', function (d, i) {
                    return $(d).attr("seriesname");
                }).style('fill', function (d, i) {
                    return $(d).css("fill");
                }).on("touchstart", function () {
                    worldMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseover", function () {
                    worldMapTooltip.onMouseOver.call(this, parentContainerId);
                }).on("mouseout", worldMapTooltip.onMouseOut).on("touchmove", worldMapTooltip.onTouchMove).on("mousemove", function () {
                    worldMapTooltip.onMouseMove.call(this, parentContainerId);
                });

                //Set auto zoom only if user has not specified zoom.
                if (setAutoZoom) leafletMap.flyToBounds([[minLat, minLon], [maxLat, maxLon]], { duration: 0.01 });

                //Custom leaflet zoom control
                $("#" + leafletContainerId).append($(leafletZoomDiv).clone());

                //Append legend
                if (legendContainer != null && legendContainer[0][0] != null) {
                    var $customLegend = $(leafletLegendDiv).clone();

                    $("#" + leafletContainerId).append($customLegend);

                    var legendScale = Math.min($customLegend.width(), $customLegend.height());
                    var legendRectSize = 0.2 * legendScale;
                    var vert = $customLegend.height() * 0.5;
                    var verticalPadding = 0.7 * legendRectSize;
                    var svgHeight = (legendRectSize + verticalPadding * 2) * legendContainer[0].length;

                    var legendSVG = d3.select("#" + leafletContainerId + " .leaflet-control-custom-legend .legend-svg").append('svg').attr("width", $customLegend.width() * 0.8).attr("height", svgHeight);

                    var m = Math.round(legendContainer[0].length / 2);

                    for (var i = 0; i < legendContainer[0].length; i++) {
                        if (i != 0) {
                            if (i % 2 == 1) m = m + i;else m = m - i;
                        }

                        var legendItem = $(legendContainer[0][i]);
                        var legendPath = $(legendItem.find("path")[0]);
                        var legendText = $(legendItem.find("text")[0]);

                        var legend = legendSVG.append("g").attr('transform', function (d) {
                            return 'translate(' + Number($customLegend.width() * 0.1) + ',' + Number(verticalPadding + i * (legendRectSize + verticalPadding)) + ')';
                        }).attr('class', 'map-legend-container');

                        legend.append('path').attr('d', d3.svg.symbol().type(function (d) {
                            var type = legendPath.attr("type");

                            if (type != undefined || type != null) return type;

                            return markerType;
                        }).size(function (d) {
                            return legendRectSize * 5;
                        })).style('fill', function (d) {
                            var color = legendPath.css("fill");
                            if (color != null && color.trim().length > 0) return color;
                        }).attr('class', function () {
                            return legendPath.attr("class");
                        }).attr("type", function (d) {
                            var type = legendPath.attr("type");

                            if (type != undefined || type != null) return type;

                            return markerType;
                        }).attr("marker-class-name", legendPath.attr("marker-class-name")).on("click", function () {
                            var selector = "#" + parentContainerId + " .map-marker." + $(this).attr("marker-class-name");

                            if (!d3.select(this).classed("inactive")) {
                                d3.select(this).classed("inactive", true);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", true);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", true);

                                d3.selectAll(selector).transition().duration(200).attr('opacity', 0).each("end", function (d) {
                                    d3.select(this).style("display", "none");
                                });
                            } else {
                                d3.select(this).classed("inactive", false);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", false);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", false);

                                d3.selectAll(selector).style("display", "");
                                d3.selectAll(selector).transition().duration(200).attr('opacity', 1);
                            }
                        });

                        var text = legend.append('text').attr('x', legendRectSize).attr('class', legendText.attr("class")).style('text-anchor', 'start').style('dominant-baseline', 'central').text(function () {
                            return legendText.text();
                        }).attr("marker-class-name", legendText.attr("marker-class-name")).on("click", function () {
                            var selector = "#" + parentContainerId + " .map-marker." + $(this).attr("marker-class-name");

                            if (!d3.select(this).classed("inactive")) {
                                d3.select(this).classed("inactive", true);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", true);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", true);

                                d3.selectAll(selector).transition().duration(200).attr('opacity', 0).each("end", function (d) {
                                    d3.select(this).style("display", "none");
                                });
                            } else {
                                d3.select(this).classed("inactive", false);

                                if (this.previousElementSibling != null) d3.select(this.previousElementSibling).classed("inactive", false);

                                if (this.nextElementSibling != null) d3.select(this.nextElementSibling).classed("inactive", false);

                                d3.selectAll(selector).style("display", "");
                                d3.selectAll(selector).transition().duration(200).attr('opacity', 1);
                            }
                        });
                    }

                    $customLegend.on("mouseover", function () {
                        leafletMap.dragging.disable();
                        leafletMap.scrollWheelZoom.disable();
                    });

                    $customLegend.on("mouseout", function () {
                        leafletMap.dragging.enable();
                        leafletMap.scrollWheelZoom.enable();
                    });
                }

                leafletMap.on("moveend", update);

                if ($("#" + containerId).is(".edit-widget-container")) $("#" + leafletContainerId).append($(mapCenterDiv).clone());

                update();

                $(document).on("click", ".map-center", function (e) {
                    if (currentWidgetConfig != null && $("#CenterCoordinates-" + currentWidgetConfig.Id) != null && centerCoordinates != null) $("#CenterCoordinates-" + currentWidgetConfig.Id).val(JSON.stringify(centerCoordinates));
                });

                //leaflet allows only one zoom control per page by default.
                //Added custom zoom events to support multiple zoom controls
                // in single page.
                $(document).on("click", ".leaflet-control-zoom-in", function (e) {
                    if ($(this).parents(".leaflet-container").attr("id") != leafletMap.getContainer().id) return;
                    leafletMap.setZoom(leafletMap.getZoom() + 1);
                    //e.preventDefault();
                });

                $(document).on("click", ".leaflet-control-zoom-out", function (e) {
                    if ($(this).parents(".leaflet-container").attr("id") != leafletMap.getContainer().id) return;
                    leafletMap.setZoom(leafletMap.getZoom() - 1);
                    //e.preventDefault();
                });
            }
        }

        function redrawMap() {
            if (zoomLevel != null) {
                projection.scale(zoomLevel);
            }

            if (centerCoordinates != null) {
                projection.rotate([-centerCoordinates[1], 0]).center([0, centerCoordinates[0]]);
            }

            if (mapViewType.toLowerCase() == "unknown" || mapViewType.toLowerCase() == "standard") {
                svg.selectAll('.geo-region') // re-project path data
                .attr('d', function (d) {
                    var t = d3.select(this);

                    var values = JSON.parse(t.attr("values"));
                    return path(values);
                });

                svg.selectAll('.geo-borders') // re-project path data
                .attr('d', function (d) {
                    var t = d3.select(this);

                    var values = JSON.parse(t.attr("values"));
                    return path(values);
                });

                svg.selectAll(".map-marker").attr("transform", function (d) {
                    var loc = JSON.parse(d3.select(this).attr("loc"));
                    return "translate(" + projection(loc)[0] + "," + projection(loc)[1] + ")";
                }).attr("d", d3.svg.symbol().type(function (d) {
                    var marker = d3.select(this).attr("marker");
                    if (marker != undefined) return marker;else return markerType;
                }).size(function (d) {
                    var markerSize = d3.select(this).attr("size");
                    return markerSize;
                }));

                svg.selectAll(".map-link").selectAll("path").attr("d", function (d) {
                    var coordinates = JSON.parse(d3.select(this).attr("coordinates"));
                    var link = {};
                    link.type = "LineString";
                    link.coordinates = coordinates;
                    return path(link);
                });
            }
        }

        function getMinMaxLatLong(bounds) {
            var topLeftCoords = projection.invert(bounds[0]);
            var botRightCoords = projection.invert(bounds[1]);

            if (topLeftCoords[1] < minLatitude) minLatitude = topLeftCoords[1];

            if (botRightCoords[1] < minLatitude) minLatitude = botRightCoords[1];

            if (topLeftCoords[1] > maxLatitude) maxLatitude = topLeftCoords[1];

            if (botRightCoords[1] > maxLatitude) maxLatitude = botRightCoords[1];

            if (topLeftCoords[0] < minLongitude) minLongitude = topLeftCoords[0];

            if (botRightCoords[0] < minLongitude) minLongitude = botRightCoords[0];

            if (topLeftCoords[0] > maxLongitude) {
                if (mapType.toLowerCase() != "world") {
                    //Max longitude should be always less than maxUSLongitude for US map.
                    if (topLeftCoords[0] < maxUSLongitude) maxLongitude = topLeftCoords[0];
                } else maxLongitude = topLeftCoords[0];
            }
            if (botRightCoords[0] > maxLongitude) {
                if (mapType.toLowerCase() != "world") {
                    //Max longitude should be always less than maxUSLongitude for US map.
                    if (botRightCoords[0] < maxUSLongitude) maxLongitude = botRightCoords[0];
                } else maxLongitude = botRightCoords[0];
            }
        }

        function getMarkerType(type) {
            if (type == null || type.trim().length == 0) return "circle";

            switch (type.toLowerCase()) {
                case "c":
                default:
                    return "circle";
                case "d":
                    return "diamond";
                case "tu":
                    return "triangle-up";
                case "td":
                    return "triangle-down";
                case "s":
                    return "square";
            }
        }
    };

    var initiateWidgetRendering = function initiateWidgetRendering(configObj) {

        // Get widget specific widgetConfig
        var widgetConfig = worldMapConfigBuilder.configBuilder.call(this, cloneDeep(configObj));
        renderWidget(widgetConfig);
    };

    return {
        renderWidget: renderWidget,
        initiateWidgetRendering: initiateWidgetRendering
    };
};

export default mapLeaflet();